// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording
// invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import type { AgeRangeDto, CountryCodeDto, InterestDto, ReferralDto } from '../api/client.js';
import { messages, useMessages, type Language } from '../i18n.js';

/**
 * The details a person states about themselves (ADR-034; widened 2026-09-13 for statistics): the
 * name Bilal uses, how Arabic addresses them, and four closed choices. Drawn once, used by the
 * sign-up form and by the card an outside-door account meets before its first conversation. Every
 * choice is a pill or a list of closed codes; the name is the only free text.
 */
const T = messages({
  ar: {
    ageRanges: {
      '18-24': '18–24',
      '25-34': '25–34',
      '35-44': '35–44',
      '45-54': '45–54',
      '55+': '55+',
    },
    countries: {
      SA: 'السعودية',
      AE: 'الإمارات',
      KW: 'الكويت',
      QA: 'قطر',
      BH: 'البحرين',
      OM: 'عُمان',
      YE: 'اليمن',
      IQ: 'العراق',
      SY: 'سوريا',
      LB: 'لبنان',
      JO: 'الأردن',
      PS: 'فلسطين',
      EG: 'مصر',
      SD: 'السودان',
      LY: 'ليبيا',
      TN: 'تونس',
      DZ: 'الجزائر',
      MA: 'المغرب',
      MR: 'موريتانيا',
      SO: 'الصومال',
      DJ: 'جيبوتي',
      KM: 'جزر القمر',
      other: 'دولة أخرى',
    },
    referrals: {
      tiktok: 'تيك توك',
      instagram: 'إنستغرام',
      friend: 'من صديق',
      search: 'بحث',
      other: 'غير ذلك',
    },
    interests: {
      faith: 'علاقتي بديني',
      work: 'العمل والدراسة',
      family: 'الأسرة والعلاقات',
      self: 'تطوير نفسي',
      other: 'شيء آخر',
    },
    nameLabel: 'ما الاسم الذي تودّ أن يناديك به بلال؟',
    genderGroup: 'كيف يخاطبك بلال؟',
    female: 'أنثى',
    male: 'ذكر',
    ageGroup: 'فئتك العمرية',
    countryLabel: 'بلدك',
    countryPrompt: 'اختر بلدك',
    referralGroup: 'كيف عرفت بلال؟',
    interestGroup: 'ما الذي جئت من أجله؟',
    problems: {
      name: 'اكتب الاسم الذي تودّ أن يناديك به بلال.',
      gender: 'اختر كيف يخاطبك بلال.',
    },
  },
  en: {
    ageRanges: {
      '18-24': '18–24',
      '25-34': '25–34',
      '35-44': '35–44',
      '45-54': '45–54',
      '55+': '55+',
    },
    countries: {
      SA: 'Saudi Arabia',
      AE: 'United Arab Emirates',
      KW: 'Kuwait',
      QA: 'Qatar',
      BH: 'Bahrain',
      OM: 'Oman',
      YE: 'Yemen',
      IQ: 'Iraq',
      SY: 'Syria',
      LB: 'Lebanon',
      JO: 'Jordan',
      PS: 'Palestine',
      EG: 'Egypt',
      SD: 'Sudan',
      LY: 'Libya',
      TN: 'Tunisia',
      DZ: 'Algeria',
      MA: 'Morocco',
      MR: 'Mauritania',
      SO: 'Somalia',
      DJ: 'Djibouti',
      KM: 'Comoros',
      other: 'Another country',
    },
    referrals: {
      tiktok: 'TikTok',
      instagram: 'Instagram',
      friend: 'From a friend',
      search: 'Search',
      other: 'Something else',
    },
    interests: {
      faith: 'My relationship with my faith',
      work: 'Work and study',
      family: 'Family and relationships',
      self: 'Self-development',
      other: 'Something else',
    },
    nameLabel: 'What would you like Bilal to call you?',
    genderGroup: 'How should Bilal address you?',
    female: 'Female',
    male: 'Male',
    ageGroup: 'Your age range',
    countryLabel: 'Your country',
    countryPrompt: 'Choose your country',
    referralGroup: 'How did you hear of Bilal?',
    interestGroup: 'What did you come for?',
    problems: {
      name: 'Write the name you would like Bilal to call you.',
      gender: 'Choose how Bilal should address you.',
    },
  },
});

/** Any letter in any script: a label without one is a number and is written left to right. */
const LETTERS = /\p{L}/u;

export interface DetailsValue {
  readonly displayName: string;
  readonly gender: 'female' | 'male' | null;
  readonly ageRange: AgeRangeDto | null;
  readonly country: CountryCodeDto | null;
  readonly referral: ReferralDto | null;
  readonly interest: InterestDto | null;
}

/**
 * One question: its label, then its answers, as a single block (founder, 2026-09-16: "organize the
 * options"). Every question in this form is drawn this way — the free-text name and the country
 * list included — so the eye can tell a label from its own control apart from the gap between one
 * question and the next. The label *is* the group's accessible name rather than a second copy of
 * it typed into `aria-label`.
 */
function Pills<T extends string>({
  id,
  label,
  options,
  value,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly options: Readonly<Record<T, string>>;
  readonly value: T | null;
  readonly onChange: (value: T) => void;
}): React.JSX.Element {
  return (
    <div className="details-field">
      <span className="details-label" id={id}>
        {label}
      </span>
      <div className="pill-set cloud" role="group" aria-labelledby={id}>
        {(Object.keys(options) as T[]).map((option) => (
          <button
            key={option}
            type="button"
            className="pill"
            aria-pressed={value === option}
            onClick={() => {
              onChange(option);
            }}
          >
            {/* A label of digits alone is laid out left to right inside its own isolate. Arabic
                reads right to left and a dash or a plus carries no direction of its own, so in the
                Arabic card "25–34" was rendering as "34–25" and "55+" as "+55" — every age range
                backwards. The isolate is on the label rather than on the button, so the chosen
                mark stays on the side the rest of the form puts it. */}
            {LETTERS.test(options[option]) ? (
              options[option]
            ) : (
              <span dir="ltr">{options[option]}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

export function DetailsFields({
  idPrefix,
  value,
  onChange,
  fields = 'all',
}: {
  readonly idPrefix: string;
  readonly value: DetailsValue;
  readonly onChange: (next: DetailsValue) => void;
  /**
   * Which questions are drawn (ADR-041). `identity`: the name and the form of address, which Bilal
   * needs before it can speak to someone. `statistics`: the four closed choices, which are a
   * person's to give or not. `all`: both, as the settings show them.
   */
  readonly fields?: 'identity' | 'statistics' | 'all';
}): React.JSX.Element {
  const t = useMessages(T);
  const set = <K extends keyof DetailsValue>(key: K, next: DetailsValue[K]): void => {
    onChange({ ...value, [key]: next });
  };
  const identity = fields !== 'statistics';
  const statistics = fields !== 'identity';
  return (
    <div className="details-fields">
      {identity && (
        <>
          <div className="details-field">
            <label htmlFor={`${idPrefix}-name`}>{t.nameLabel}</label>
            <input
              id={`${idPrefix}-name`}
              type="text"
              autoComplete="nickname"
              maxLength={60}
              value={value.displayName}
              onChange={(event) => {
                set('displayName', event.target.value);
              }}
            />
          </div>
          <div className="details-field">
            {/* The one question that had no visible label: it was named for a screen reader and
            unnamed on the screen, so the two pills sat under the name field as if they belonged
            to it. */}
            <span className="details-label" id={`${idPrefix}-gender-label`}>
              {t.genderGroup}
            </span>
            <div
              className="pill-set binary"
              role="group"
              aria-labelledby={`${idPrefix}-gender-label`}
            >
              <button
                type="button"
                className="pill"
                aria-pressed={value.gender === 'female'}
                onClick={() => {
                  set('gender', 'female');
                }}
              >
                {t.female}
              </button>
              <button
                type="button"
                className="pill"
                aria-pressed={value.gender === 'male'}
                onClick={() => {
                  set('gender', 'male');
                }}
              >
                {t.male}
              </button>
            </div>
          </div>
        </>
      )}
      {statistics && (
        <>
          <Pills
            id={`${idPrefix}-age-label`}
            label={t.ageGroup}
            options={t.ageRanges}
            value={value.ageRange}
            onChange={(next) => {
              set('ageRange', next);
            }}
          />
          <div className="details-field">
            <label htmlFor={`${idPrefix}-country`}>{t.countryLabel}</label>
            <select
              id={`${idPrefix}-country`}
              className="country-select"
              value={value.country ?? ''}
              onChange={(event) => {
                const next = event.target.value;
                set('country', next === '' ? null : (next as CountryCodeDto));
              }}
            >
              <option value="">{t.countryPrompt}</option>
              {(Object.keys(t.countries) as CountryCodeDto[]).map((code) => (
                <option key={code} value={code}>
                  {t.countries[code]}
                </option>
              ))}
            </select>
          </div>
          <Pills
            id={`${idPrefix}-referral-label`}
            label={t.referralGroup}
            options={t.referrals}
            value={value.referral}
            onChange={(next) => {
              set('referral', next);
            }}
          />
          <Pills
            id={`${idPrefix}-interest-label`}
            label={t.interestGroup}
            options={t.interests}
            value={value.interest}
            onChange={(next) => {
              set('interest', next);
            }}
          />
        </>
      )}
    </div>
  );
}

export interface StatedDetails {
  readonly displayName: string;
  readonly gender: 'female' | 'male';
  readonly ageRange?: AgeRangeDto;
  readonly country?: CountryCodeDto;
  readonly referral?: ReferralDto;
  readonly interest?: InterestDto;
}

/**
 * The name and the form of address, trimmed and typed — or the first of the two missing, as the
 * person's words. The four statistics choices ride along when they were made and are never asked
 * for (ADR-041): they used to be required, and a form that long stood between a person and the
 * first conversation.
 */
export function statedDetailsOf(
  value: DetailsValue,
  language: Language = 'ar',
):
  | { readonly ok: true; readonly details: StatedDetails }
  | { readonly ok: false; readonly problem: string } {
  const displayName = value.displayName.trim();
  if (displayName.length === 0) return { ok: false, problem: T[language].problems.name };
  if (value.gender === null) return { ok: false, problem: T[language].problems.gender };
  return {
    ok: true,
    details: {
      displayName,
      gender: value.gender,
      ...(value.ageRange === null ? {} : { ageRange: value.ageRange }),
      ...(value.country === null ? {} : { country: value.country }),
      ...(value.referral === null ? {} : { referral: value.referral }),
      ...(value.interest === null ? {} : { interest: value.interest }),
    },
  };
}
